import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Trophy, GraduationCap, BookOpen, Dumbbell, Compass, MapPin, Clock,
  CheckCircle2, X, Flame, Crown, Loader2, Share2, Lightbulb, Camera,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { FoundingBadge } from "@/components/FoundingBadge";
import { InnovatorBadge } from "@/components/InnovatorBadge";
import { IdeasBoard } from "@/components/IdeasBoard";
import PastTerms from "@/components/PastTerms";

const BADGE_ICON = {
  "graduation-cap": GraduationCap,
  "book-open": BookOpen,
  "dumbbell": Dumbbell,
  "compass": Compass,
};
const KIND_META = {
  class: { label: "Class", icon: GraduationCap },
  library: { label: "Library", icon: BookOpen },
  rec: { label: "Rec Center", icon: Dumbbell },
};
const TIER_STYLE = {
  bronze: { ring: "#b45309", bg: "#b45309", label: "Bronze" },
  silver: { ring: "#64748b", bg: "#64748b", label: "Silver" },
  gold: { ring: "#d97706", bg: "#d97706", label: "Gold" },
};

const getPos = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("no geolocation"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve([p.coords.latitude, p.coords.longitude]),
      (e) => reject(e),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  });

const mmss = (mins) => {
  const total = Math.max(0, Math.round(mins * 60));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export default function Rewards() {
  const { user, checkAuth } = useAuth();
  const avatarRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const onAvatarPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAvatarBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setAvatarBusy(false);
    e.target.value = "";
  };
  const [me, setMe] = useState(null);
  const [nearby, setNearby] = useState(null);
  const [active, setActive] = useState(null);
  const [elapsed, setElapsed] = useState(0); // minutes, live
  const [loc, setLoc] = useState(null);
  const [scope, setScope] = useState("weekly");
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState("");
  const tick = useRef(null);

  const loadMe = useCallback(() => api.get("/gamification/me").then((r) => setMe(r.data)), []);
  const loadBoard = useCallback(
    (sc) => api.get(`/gamification/leaderboard?scope=${sc}`).then((r) => setBoard(r.data)),
    []
  );

  const loadActive = useCallback(async () => {
    const r = await api.get("/checkins/active");
    setActive(r.data.active);
    if (r.data.active) setElapsed(r.data.active.elapsed_min);
  }, []);

  const loadNearby = useCallback(async (l) => {
    try {
      const r = await api.get(`/checkins/nearby?lat=${l[0]}&lng=${l[1]}`);
      setNearby(r.data.places);
    } catch { setNearby({ class: [], library: [], rec: [] }); }
  }, []);

  useEffect(() => {
    loadMe();
    loadBoard("weekly");
    loadActive();
    getPos().then((l) => { setLoc(l); loadNearby(l); }).catch(() => setNearby(null));
  }, [loadMe, loadBoard, loadActive, loadNearby]);

  useEffect(() => { loadBoard(scope); }, [scope, loadBoard]);

  // Live 1s timer for the active check-in
  useEffect(() => {
    if (!active) { clearInterval(tick.current); return; }
    const started = new Date(active.started_at).getTime();
    tick.current = setInterval(() => {
      setElapsed((Date.now() - started) / 60000);
    }, 1000);
    return () => clearInterval(tick.current);
  }, [active]);

  const ready = elapsed >= (active?.min_minutes || 30);

  const startCheckin = async (kind, place) => {
    setBusy(place.id);
    try {
      const l = await getPos();
      setLoc(l);
      const r = await api.post("/checkins/start", { kind, place_id: place.id, lat: l[0], lng: l[1] });
      setActive(r.data);
      setElapsed(0);
      toast.success(`Checked in at ${place.name}. Stay ${r.data.min_minutes} min to earn ${r.data.points_preview} pts.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check in.");
    } finally { setBusy(""); }
  };

  const checkout = async () => {
    if (!active) return;
    setBusy("checkout");
    try {
      const l = await getPos();
      const r = await api.post(`/checkins/${active.id}/complete`, { lat: l[0], lng: l[1] });
      if (r.data.awarded) {
        toast.success(`+${r.data.points} pts for ${r.data.place_name}!`);
        (r.data.new_badges || []).forEach((b) =>
          toast.success(`🏅 New badge: ${b.name} (${TIER_STYLE[b.tier].label})`, { duration: 6000 }));
        setActive(null);
        await Promise.all([loadMe(), loadBoard(scope), loadNearby(l)]);
      } else {
        toast.message(`Stay ${r.data.remaining_min} more min to earn points.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check out.");
    } finally { setBusy(""); }
  };

  const cancel = async () => {
    if (!active) return;
    setBusy("cancel");
    try {
      await api.post(`/checkins/${active.id}/cancel`);
      setActive(null);
      toast.message("Check-in cancelled.");
    } catch { toast.error("Could not cancel."); }
    finally { setBusy(""); }
  };

  const lvl = me?.level;
  const nearbyCount = nearby ? Object.values(nearby).reduce((a, v) => a + v.length, 0) : 0;

  const shareRank = async () => {
    const myRow = board?.rows?.find((r) => r.is_me);
    const rank = myRow?.rank;
    const cv = document.createElement("canvas");
    cv.width = 1080; cv.height = 1080;
    const ctx = cv.getContext("2d");
    const brand = (getComputedStyle(document.documentElement).getPropertyValue("--brand-rgb") || "80,0,0").trim();
    ctx.fillStyle = `rgb(${brand})`; ctx.fillRect(0, 0, 1080, 1080);
    const center = (t, y, font, color = "#ffffff") => {
      ctx.fillStyle = color; ctx.font = font; ctx.textAlign = "center";
      ctx.fillText(t, 540, y);
    };
    ctx.textAlign = "left";
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = "700 46px Manrope, Arial, sans-serif";
    ctx.fillText("jeeo", 80, 130);
    ctx.font = "500 30px Manrope, Arial, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.fillText((user?.school_name || "Campus").toUpperCase(), 80, 180);
    center(rank ? `#${rank}` : "—", 500, "800 260px Manrope, Arial, sans-serif");
    center(`of my crew ${scope === "weekly" ? "this week" : "all-time"}`, 580, "500 40px Manrope, Arial, sans-serif", "rgba(255,255,255,0.85)");
    center(`${me?.points ?? 0} points`, 740, "700 64px Manrope, Arial, sans-serif");
    center(`Level ${lvl?.level} · ${lvl?.title}${me?.streak ? `  ·  🔥 ${me.streak}d streak` : ""}`, 800, "500 34px Manrope, Arial, sans-serif", "rgba(255,255,255,0.8)");
    center("your day • your crew • your campus", 990, "500 30px Manrope, Arial, sans-serif", "rgba(255,255,255,0.6)");
    cv.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "routerev-rank.png", { type: "image/png" });
      const text = `I'm ${rank ? `#${rank}` : "on the board"} on jeeo with ${me?.points ?? 0} pts! 🚌🏆`;
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "My jeeo rank", text });
          return;
        }
      } catch { /* fall through to download */ }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "routerev-rank.png"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Rank card saved — share it anywhere!");
    }, "image/png");
  };

  return (
    <AppShell>
      <div className="px-4 lg:px-6 py-5 max-w-[1100px] mx-auto" data-testid="rewards-page">
        <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Earn as you go</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-4 flex items-center gap-2">
          <Trophy className="text-brand" size={30} strokeWidth={1.6} /> Rewards
        </h1>

        {/* Level + points header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-brand text-white p-5 mb-5 relative overflow-hidden"
          data-testid="rewards-summary"
        >
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <button type="button" data-testid="avatar-upload-btn" onClick={() => avatarRef.current?.click()}
                disabled={avatarBusy} className="relative shrink-0 rounded-full">
                <Avatar src={user?.picture} name={user?.name} size={64} className="ring-2 ring-white/50" />
                <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white text-brand flex items-center justify-center shadow ring-2 ring-brand/10">
                  <Camera size={13} strokeWidth={2} />
                </span>
                <input ref={avatarRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden" onChange={onAvatarPick} data-testid="avatar-file-input" />
              </button>
              <div>
                <div className="mono text-[11px] tracking-widest uppercase opacity-80">Level {lvl?.level || 1}</div>
                <div className="font-display text-2xl font-semibold">{lvl?.title || "Newcomer"}</div>
                {(user?.founding_member || user?.innovator) && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {user?.founding_member && <FoundingBadge />}
                    {user?.innovator && <InnovatorBadge />}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-4xl font-bold leading-none" data-testid="rewards-points">
                {me?.points ?? 0}
              </div>
              <div className="mono text-[11px] tracking-widest uppercase opacity-80">points</div>
            </div>
          </div>
          <div className="mt-4">
            <div className="h-2 rounded-full bg-white/25 overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all"
                   style={{ width: `${Math.round((lvl?.progress || 0) * 100)}%` }} />
            </div>
            <div className="mono text-[11px] mt-1 opacity-80">
              {lvl?.next_points != null
                ? `${lvl.into}/${lvl.span} to Level ${lvl.level + 1}`
                : "Max level reached"}
            </div>
          </div>
          <div className="mt-4 flex gap-4 text-sm flex-wrap items-center">
            <span className="flex items-center gap-1.5"><GraduationCap size={15} /> {me?.counts?.class || 0} classes</span>
            <span className="flex items-center gap-1.5"><BookOpen size={15} /> {me?.counts?.library || 0} library</span>
            <span className="flex items-center gap-1.5"><Dumbbell size={15} /> {me?.counts?.rec || 0} rec</span>
            {me?.streak > 0 && (
              <span className="flex items-center gap-1.5 font-bold bg-white/15 rounded-full px-2.5 py-0.5"
                    data-testid="rewards-streak">
                <Flame size={15} /> {me.streak}-day streak
              </span>
            )}
          </div>
        </motion.div>

        {/* Active check-in OR check-in nearby */}
        {active ? (
          <div className="rounded-2xl border-2 border-brand bg-brand/5 p-5 mb-6" data-testid="active-checkin">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin className="text-brand" size={18} />
                <div>
                  <div className="font-semibold">{active.place_name}</div>
                  <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    {KIND_META[active.kind]?.label} check-in
                  </div>
                </div>
              </div>
              <span className={`mono text-2xl font-bold ${ready ? "text-[#166534]" : "text-brand"}`}
                    data-testid="checkin-timer">
                {ready ? "Ready!" : mmss(elapsed)}
              </span>
            </div>
            <div className="h-2 rounded-full bg-brand/15 overflow-hidden mb-4">
              <div className="h-full bg-brand rounded-full transition-all"
                   style={{ width: `${Math.min(100, (elapsed / (active.min_minutes || 30)) * 100)}%` }} />
            </div>
            <div className="flex gap-2">
              <button
                onClick={checkout} disabled={!ready || busy === "checkout"}
                data-testid="checkout-btn"
                className={`flex-1 h-11 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition
                  ${ready ? "bg-brand text-white hover:opacity-90" : "bg-muted text-muted-foreground cursor-not-allowed"}`}
              >
                {busy === "checkout" ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                {ready ? `Check out · +${(active.points_preview || (active.kind === "class" ? 10 : 8))} pts` : `Stay ${mmss((active.min_minutes || 30) - elapsed)} more`}
              </button>
              <button onClick={cancel} disabled={busy === "cancel"} data-testid="cancel-checkin-btn"
                      className="h-11 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted flex items-center gap-1.5">
                <X size={15} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mb-6" data-testid="checkin-nearby">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-lg font-semibold">Check in nearby</h2>
              <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">
                within 150 m · stay 30 min
              </span>
            </div>
            {nearby === null ? (
              <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                Enable location to see check-in spots around you.
              </div>
            ) : nearbyCount === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                No check-in spots within range. Walk up to a class building, the library, or the rec center to earn points.
              </div>
            ) : (
              <div className="space-y-4">
                {["class", "library", "rec"].map((kind) =>
                  (nearby[kind] || []).length === 0 ? null : (
                    <div key={kind}>
                      <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1.5">
                        {(() => { const I = KIND_META[kind].icon; return <I size={13} />; })()}
                        {KIND_META[kind].label} · {kind === "class" ? 10 : 8} pts
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {nearby[kind].map((p) => (
                          <button
                            key={p.id} onClick={() => startCheckin(kind, p)} disabled={busy === p.id}
                            data-testid={`checkin-${kind}-${p.id}`}
                            className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card p-3 text-left hover:border-brand transition"
                          >
                            <span className="min-w-0">
                              <span className="block font-medium text-sm truncate">{p.name}</span>
                              <span className="mono text-[11px] text-muted-foreground">{p.distance_m} m away</span>
                            </span>
                            {busy === p.id
                              ? <Loader2 className="animate-spin text-brand shrink-0" size={16} />
                              : <span className="mono text-[11px] font-bold text-brand shrink-0">I'M HERE</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {/* Badges */}
        <h2 className="font-display text-lg font-semibold mb-2">Badges</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="badges-grid">
          {(me?.badges || []).map((b) => {
            const I = BADGE_ICON[b.icon] || Trophy;
            const style = b.tier ? TIER_STYLE[b.tier] : null;
            return (
              <div key={b.key} data-testid={`badge-${b.key}`}
                   className={`rounded-2xl border p-4 text-center transition ${b.earned ? "border-transparent" : "border-border opacity-70"}`}
                   style={b.earned ? { boxShadow: `0 0 0 2px ${style.ring} inset` } : {}}>
                <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2"
                     style={{ background: b.earned ? style.bg : "rgba(0,0,0,0.06)", color: b.earned ? "#fff" : "#9ca3af" }}>
                  <I size={22} strokeWidth={1.6} />
                </div>
                <div className="font-semibold text-sm">{b.name}</div>
                <div className="mono text-[11px] uppercase tracking-widest mb-2"
                     style={{ color: b.earned ? style.ring : "#9ca3af" }}>
                  {b.tier ? style.label : "Locked"}
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                       style={{ width: `${Math.round(b.progress * 100)}%`, background: style ? style.bg : "rgb(var(--brand-rgb))" }} />
                </div>
                <div className="mono text-[11px] text-muted-foreground mt-1">
                  {b.next_tier ? `${b.count}/${b.next_threshold} → ${TIER_STYLE[b.next_tier].label}` : `${b.count} · maxed`}
                </div>
              </div>
            );
          })}
        </div>

        {/* Special badges — permanent, aspirational (shown locked until earned) */}
        <h2 className="font-display text-lg font-semibold mb-2">Special badges</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="special-badges-grid">
          {[
            { key: "founding", name: "Founding Member", icon: Crown, earned: !!user?.founding_member,
              grad: "from-amber-300 to-amber-500", ring: "#f59e0b",
              hint: "Awarded to the first 25 students at your campus." },
            { key: "innovator", name: "Innovator", icon: Lightbulb, earned: !!user?.innovator,
              grad: "from-emerald-300 to-teal-500", ring: "#10b981",
              hint: "Post an idea on the Ideas board — earn it when it ships." },
          ].map((b) => {
            const I = b.icon;
            return (
              <div key={b.key} data-testid={`special-badge-${b.key}`}
                   className={`rounded-2xl border p-4 flex items-center gap-3 transition ${b.earned ? "border-transparent bg-white" : "border-border opacity-70 bg-white"}`}
                   style={b.earned ? { boxShadow: `0 0 0 2px ${b.ring} inset` } : {}}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${b.earned ? `bg-gradient-to-br ${b.grad} text-white` : "bg-black/[0.06] text-muted-foreground grayscale"}`}>
                  <I size={22} strokeWidth={1.7} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    {b.name}
                    <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                          style={{ color: b.earned ? b.ring : "#9ca3af", background: b.earned ? `${b.ring}1a` : "rgba(0,0,0,0.05)" }}
                          data-testid={`special-badge-state-${b.key}`}>
                      {b.earned ? "Earned" : "Locked"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{b.hint}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Crown className="text-brand" size={18} /> Friends leaderboard
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={shareRank} data-testid="share-rank-btn"
                    className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-brand text-white text-xs font-semibold hover:opacity-90">
              <Share2 size={14} /> Share my rank
            </button>
            <div className="flex rounded-lg border border-border overflow-hidden text-xs">
              {["weekly", "alltime"].map((s) => (
                <button key={s} onClick={() => setScope(s)} data-testid={`lb-scope-${s}`}
                        className={`px-3 py-1.5 font-medium ${scope === s ? "bg-brand text-white" : "hover:bg-muted"}`}>
                  {s === "weekly" ? "This week" : "All time"}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-border overflow-hidden mb-6" data-testid="leaderboard">
          {(board?.rows || []).map((r) => (
            <div key={r.user_id} data-testid={`lb-row-${r.rank}`}
                 className={`flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 ${r.is_me ? "bg-brand/5" : ""}`}>
              <span className={`mono text-sm font-bold w-6 text-center ${r.rank <= 3 ? "text-brand" : "text-muted-foreground"}`}>
                {r.rank}
              </span>
              {r.picture
                ? <img src={r.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                : <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold">{(r.name || "?")[0]}</span>}
              <span className="flex-1 font-medium text-sm truncate flex items-center gap-1.5">
                <span className="truncate">{r.name}{r.is_me && <span className="text-brand"> (you)</span>}</span>
                {r.founding_member && <FoundingBadge compact />}
              </span>
              <span className="mono text-sm font-bold text-brand">{r.points}</span>
            </div>
          ))}
          {board && board.rows.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground">Add friends to compete on the leaderboard.</div>
          )}
        </div>

        {/* History */}
        {(me?.history || []).length > 0 && (
          <>
            <h2 className="font-display text-lg font-semibold mb-2 flex items-center gap-2">
              <Clock size={17} /> Recent check-ins
            </h2>
            <div className="rounded-2xl border border-border overflow-hidden" data-testid="checkin-history">
              {me.history.map((h, i) => {
                const I = KIND_META[h.kind]?.icon || Flame;
                return (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0">
                    <I size={16} className="text-brand" />
                    <span className="flex-1 text-sm truncate">{h.place_name}</span>
                    <span className="mono text-xs text-muted-foreground">
                      {h.completed_at ? new Date(h.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                    </span>
                    <span className="mono text-xs font-bold text-[#166534]">+{h.points}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <PastTerms />

        <div className="mt-8 pt-6 border-t border-border">
          <IdeasBoard />
        </div>
      </div>
    </AppShell>
  );
}
